import * as THREE from "three";

// Exported accessors for the "ZUpGroup" -- the group in LHQ/Scene.tsx that wraps all z-up content, with
// rotation [-PI/2, 0, 0] (converting content-up (0,0,1) -> root-up (0,1,0)).
//
// Components that live INSIDE the ZUpGroup but consume WORLD/root-space values (controller pos/dir,
// raycast hits, XR poses...) must convert at the boundary before applying them to local transforms:
//		- points:  ZUpGroup.worldToLocal(point.clone())
//		- quats:   q_parentWorldInv.multiplyQuat(q_world)  (see ConvertWorldQuatToZUpGroupLocal below)
// Use these accessors instead of traversing .parent chains, since there is exactly one ZUpGroup and its
// position in the hierarchy shouldn't be load-bearing for correctness.

export const ZUpGroupHolder = {
	/** the ZUpGroup itself (set once LHQScene mounts; null until then) */
	group: null as THREE.Group|null,
	/** cached inverse of the group's world matrix, refreshed each frame by LHQScene -- use for world->local point conversion */
	matrixWorldInverse_cache: new THREE.Matrix4(),
	RefreshWorldMatrixInverse() {
		const group = ZUpGroupHolder.group;
		if (group == null) return;
		group.updateWorldMatrix(true, false);
		ZUpGroupHolder.matrixWorldInverse_cache.copy(group.matrixWorld).invert();
	},
	/** converts a world-space point to the ZUpGroup's local space */
	WorldPosToLocal(worldPos: THREE.Vector3): THREE.Vector3 {
		AssertGroupLoaded();
		return worldPos.clone().applyMatrix4(ZUpGroupHolder.matrixWorldInverse_cache);
	},
	/** converts a ZUpGroup-local (z-up) point to world/root space */
	LocalPosToWorld(localPos: THREE.Vector3): THREE.Vector3 {
		AssertGroupLoaded();
		return localPos.clone().applyMatrix4(ZUpGroupHolder.group!.matrixWorld);
	},
	/** converts a world-space rotation to the ZUpGroup's local space (child-local quaternion == q_parentWorldInv * q_world) */
	WorldQuatToLocal(worldQuat: THREE.Quaternion): THREE.Quaternion {
		AssertGroupLoaded();
		const group = ZUpGroupHolder.group!;
		const q_groupWorldInv = new THREE.Quaternion();
		group.getWorldQuaternion(q_groupWorldInv).invert();
		return q_groupWorldInv.multiply(worldQuat);
	},
};
function AssertGroupLoaded() {
	if (ZUpGroupHolder.group == null) throw new Error("ZUpGroupHolder.group is null; either LHQScene has not mounted yet, or the ZUpGroup ref was not registered.");
	return true;
}
